import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatAZN, pickLocale } from "../lib/format";
import { SmallCaps, BrassRule, PillCTA } from "../components/atelier/primitives";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { DELIVERY, policyText } from "../content/policies";
import { useI18n } from "../lib/i18n";
import { useCatalogSettings } from "../hooks/useCatalogSettings";
import { useDocumentTitle } from "../lib/useDocumentTitle";

const STAGES = ["pending", "confirmed", "prepared", "shipped", "delivered"];
const STAGE_LABEL = {
  pending: "Verildi",
  confirmed: "Təsdiqləndi",
  prepared: "Hazırlanır",
  shipped: "Yolda",
  delivered: "Çatdırıldı",
};

const PAYMENT_STATUS_LABEL = {
  paid: "Ödənildi",
  captured: "Ödənildi",
  authorized: "Təsdiqləndi",
  pending: "Gözləyir",
  failed: "Uğursuz",
  refunded: "Geri qaytarıldı",
  cancelled: "Ləğv edildi",
};

export default function OrderTracking() {
  const { t } = useI18n();
  const catalogSettings = useCatalogSettings();
  const deliveryLine = pickLocale(catalogSettings?.delivery_info, "az")
    || policyText(DELIVERY.short, "az");
  useDocumentTitle(t("order.doc_title", "Sifariş"));
  const { id } = useParams();
  const [sp] = useSearchParams();
  const justPlaced = sp.get("just_placed") === "1";
  const guestEmail = sp.get("email");
  const guestPhone = sp.get("phone");
  const { user, ready } = useAuth();
  const [order, setOrder] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!ready) return;
    (async () => {
      try {
        setNotFound(false);
        if (user) {
          const [o, t] = await Promise.all([
            api.get(`/me/orders/${id}`),
            api.get(`/me/orders/${id}/tracking`),
          ]);
          setOrder(o.data); setTracking(t.data);
          return;
        }
        if (guestEmail || guestPhone) {
          const o = await api.get("/orders/lookup", { params: { order_id: id, email: guestEmail, phone: guestPhone } });
          setOrder(o.data);
          setTracking({
            timeline: [
              {
                id: "guest-status",
                from: null,
                to: o.data.status,
                note: "Əlaqə təsdiqləndi — ictimai sifariş statusu",
                at: o.data.placed_at || new Date().toISOString(),
              },
            ],
          });
        }
      } catch (err) {
        console.error("[atelier] order tracking fetch failed", err);
        setNotFound(true);
      }
    })();
  }, [id, user, ready, guestEmail, guestPhone]);

  if (!ready) return <div className="bg-ivory min-h-screen"><Header /></div>;
  if (!user && !guestEmail && !guestPhone) return <Navigate to={`/auth/login?next=/orders/${id}`} replace />;
  if (notFound) {
    return (
      <div className="bg-ivory min-h-screen text-graphite">
        <Header />
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12 py-32 text-center">
          <SmallCaps>{t("order.not_found", "Sifariş tapılmadı")}</SmallCaps>
          <div className="font-serif italic text-3xl mt-4">{t("order.not_verified", "Bu sifarişi təsdiqləyə bilmədik.")}</div>
          <PillCTA as={Link} to="/account?tab=orders" variant="secondary" className="mt-8">{t("order.back_to_account", "Hesaba qayıt")}</PillCTA>
        </div>
        <Footer />
      </div>
    );
  }
  if (!order) return <div className="bg-ivory min-h-screen"><Header /><div className="mx-auto max-w-[1440px] px-6 lg:px-12 py-32 text-center font-serif italic text-graphite/70">Ateliye qeydi axtarılır…</div></div>;

  const stageIdx = STAGES.indexOf(order.status);
  const isCancelled = order.status === "cancelled" || order.status === "returned";

  return (
    <div className="bg-ivory text-graphite" data-testid="order-tracking-page">
      <Header />

      {justPlaced && (
        <div className="bg-espresso/10 border-y border-espresso/40 py-4 text-center" data-testid="order-just-placed">
          <SmallCaps>Təsdiqləndi</SmallCaps>
          <div className="font-serif italic text-2xl mt-1">{t("order.with_atelier", "Sifarişiniz ateliyedədir.")}</div>
        </div>
      )}

      <section className="mx-auto max-w-[1440px] px-6 lg:px-12 pt-14 pb-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          <SmallCaps>Sifariş</SmallCaps>
          <h1 className="font-serif italic text-5xl lg:text-6xl mt-3" data-testid="order-heading">
            #{order.display_id}
          </h1>
          <div className="mt-3 text-graphite/75">
            Sifariş tarixi: {new Date(order.placed_at || Date.now()).toLocaleDateString("az-AZ")}
          </div>
        </div>
        <div className="lg:col-span-4 lg:text-right">
          <SmallCaps>Total</SmallCaps>
          <div className="font-serif text-4xl mt-2">{formatAZN(order.total)}</div>
          <div className="text-[11px] uppercase tracking-widest text-graphite/70 mt-1">
            {order.payment_provider} · {PAYMENT_STATUS_LABEL[order.payment_status] || order.payment_status}
          </div>
        </div>
      </section>

      {/* Progress timeline */}
      <section className="mx-auto max-w-[1440px] px-6 lg:px-12 py-10" data-testid="order-timeline">
        <div className="relative">
          <div className="absolute left-0 right-0 top-3 h-px bg-graphite/15" />
          {!isCancelled && (
            <div
              className="absolute left-0 top-3 h-px bg-espresso transition-all duration-700"
              style={{ width: `${stageIdx >= 0 ? ((stageIdx) / (STAGES.length - 1)) * 100 : 0}%` }}
            />
          )}
          <div className="relative grid grid-cols-5">
            {STAGES.map((s, i) => {
              const done = !isCancelled && i <= stageIdx;
              return (
                <div key={s} className="flex flex-col items-center" data-testid={`stage-${s}`}>
                  <div className={`w-6 h-6 rounded-full border-2 ${done ? "border-espresso bg-espresso" : "border-graphite/25 bg-ivory"} z-10`} />
                  <div className={`mt-3 text-[11px] uppercase tracking-widest ${done ? "text-graphite" : "text-graphite/70"}`}>
                    {STAGE_LABEL[s]}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {isCancelled && (
          <div className="text-center mt-8 font-serif italic text-burgundy" data-testid="order-cancelled">
            {order.status === "returned" ? "Bu sifariş geri qaytarıldı." : "Bu sifariş ləğv edildi."}
          </div>
        )}
      </section>

      {/* Items + address grid */}
      <section className="mx-auto max-w-[1440px] px-6 lg:px-12 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8" data-testid="order-items">
          <SmallCaps>{t("order.pieces", "Parçalar")}</SmallCaps>
          {(order.items || []).length > 0 ? (
          <div className="mt-5 divide-y divide-graphite/10">
            {(order.items || []).map((it) => (
              <div key={it.id} className="grid grid-cols-12 gap-4 py-5 items-center">
                <div className="col-span-6">
                  <div className="font-serif text-xl">{pickLocale(it.title_snapshot)}</div>
                  <div className="text-[11px] uppercase tracking-widest text-graphite/70 mt-1">SKU {it.sku}</div>
                </div>
                <div className="col-span-3 text-graphite/75">× {it.quantity}</div>
                <div className="col-span-3 text-right font-serif text-lg">{formatAZN(it.total)}</div>
              </div>
            ))}
          </div>
          ) : (
            <div className="mt-5 border border-graphite/15 bg-ivory/30 p-6 text-sm text-graphite/75" data-testid="guest-order-limited">
              Qonaq məxfiliyi üçün məhsul təfərrüatları yalnız daxil olduqdan sonra göstərilir. Bu səhifə statusu çatdırılmaya qədər izləyir.
            </div>
          )}

          <div className="mt-12">
            <SmallCaps>{t("order.timeline", "Vaxt xətti")}</SmallCaps>
            <div className="mt-4 space-y-3">
              {(tracking?.timeline || []).map((e) => (
                <div key={e.id} className="grid grid-cols-12 gap-3 items-baseline">
                  <div className="col-span-4 text-[11px] uppercase tracking-widest text-graphite/70">{new Date(e.at).toLocaleString("az-AZ")}</div>
                  <div className="col-span-8">
                    <div className="font-serif">
                      {e.from ? `${STAGE_LABEL[e.from] || e.from} → ` : "Sifariş yaradıldı — "}
                      <span className="text-espresso">{STAGE_LABEL[e.to] || e.to}</span>
                    </div>
                    {e.note && <div className="text-graphite/70 text-sm">{e.note}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-4 space-y-6">
          <div className="border border-espresso/40 bg-stone/20 p-6" data-testid="order-shipping">
            <SmallCaps>{t("order.shipping_to", "Çatdırılma ünvanı")}</SmallCaps>
            <div className="mt-3 font-serif">{order.shipping_address?.first_name} {order.shipping_address?.last_name}</div>
            <div className="text-graphite/75 text-sm">
              {order.shipping_address?.line1}<br />
              {order.shipping_address?.city}, {order.shipping_address?.country}
              {order.shipping_address?.phone && <><br />{order.shipping_address.phone}</>}
            </div>
          </div>
          <div className="border border-graphite/15 p-6" data-testid="order-summary">
            <SmallCaps>{t("order.summary", "Xülasə")}</SmallCaps>
            <div className="mt-4 space-y-2 text-sm text-graphite/85">
              <div className="flex justify-between"><span>Cəmi</span><span>{formatAZN(order.subtotal)}</span></div>
              {(order.discount_total || 0) - (order.points_discount_minor || 0) > 0 && (
                <div className="flex justify-between"><span>{t("order.discount", "Endirim")}</span><span>− {formatAZN((order.discount_total || 0) - (order.points_discount_minor || 0))}</span></div>
              )}
              {(order.points_discount_minor || 0) > 0 && (
                <div className="flex justify-between" data-testid="order-points">
                  <span>{t("order.points", "Falcon xalları")}{order.points_spent ? ` (${order.points_spent} xal)` : ""}</span>
                  <span>− {formatAZN(order.points_discount_minor)}</span>
                </div>
              )}
              {(order.gift_card_total || 0) > 0 && (
                <div className="flex justify-between"><span>{t("order.gift_card", "Hədiyyə kartı")}</span><span>− {formatAZN(order.gift_card_total)}</span></div>
              )}
              {/* The fee, not the policy sentence: with prose here the lines
                  never summed to the total and the 10 ₼ hid inside it. */}
              <div className="flex justify-between"><span>{t("order.delivery", "Çatdırılma")}</span><span>{(order.shipping_total || 0) > 0 ? formatAZN(order.shipping_total) : t("checkout.delivery.free", "Ödənişsiz")}</span></div>
              <BrassRule className="my-3" />
              <div className="flex justify-between items-baseline">
                <span className="text-[11px] uppercase tracking-widest">Total</span>
                <span className="font-serif text-2xl">{formatAZN(order.total)}</span>
              </div>
            </div>
          </div>
          <PillCTA as={Link} to={user ? "/account?tab=orders" : "/shop"} variant="secondary" className="w-full" data-testid="back-to-orders">
            {user ? "Sifarişlərə qayıt" : "Alış-verişə davam et"}
          </PillCTA>
        </div>
      </section>
      <Footer />
    </div>
  );
}
